/**
 * MobileNavDrawer.js - Slide-out mobile navigation drawer
 * 
 * Features:
 * - Slides in from left side
 * - Backdrop overlay with click-to-close
 * - Touch-friendly navigation items (44px+ tap targets)
 * - Smooth CSS transitions
 * - Role-aware menu items (admin vs teacher)
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

function MobileNavDrawer({ isOpen, onClose }) {
  const { user, logout, isAdmin } = useAuth();
  const { theme, colors, darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle navigation
  const handleNavClick = (path) => {
    navigate(path);
    onClose();
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose();
  };

  // Check if path is active
  const isActive = (path) => {
    if (path === '/admin' || path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.includes(path);
  };

  // Teacher menu items
  const teacherMenuItems = [
    { path: '/dashboard', icon: '📅', label: 'My Schedule' },
    { path: '/availability', icon: '📋', label: 'Availability' },
    { path: '/pto', icon: '🏖️', label: 'Time Off' },
    { path: '/profile', icon: '👤', label: 'My Profile' }
  ];

  // Admin menu items (mobile - limited set)
  const adminMenuItems = [
    { path: '/admin', icon: '🏠', label: 'Dashboard' },
    { path: '/admin/audit', icon: '📝', label: 'Audit Log' },
    { path: '/profile', icon: '👤', label: 'My Profile' }
  ];

  const menuItems = isAdmin ? adminMenuItems : teacherMenuItems;

  // =============================================
  // STYLES
  // =============================================

  const backdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 998,
    opacity: isOpen ? 1 : 0,
    visibility: isOpen ? 'visible' : 'hidden',
    transition: 'opacity 0.3s ease, visibility 0.3s ease'
  };

  const drawerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    width: '280px',
    maxWidth: '85vw',
    backgroundColor: theme.cardBackground,
    zIndex: 999,
    transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: isOpen ? '4px 0 20px rgba(0, 0, 0, 0.15)' : 'none'
  };

  const headerStyle = {
    padding: '20px 16px',
    borderBottom: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  };

  const logoStyle = {
    width: '40px',
    height: '40px',
    objectFit: 'contain'
  };

  const headerTextStyle = {
    display: 'flex',
    flexDirection: 'column'
  };

  const titleStyle = {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: theme.text,
    margin: 0
  };

  const subtitleStyle = {
    fontSize: '0.8rem',
    color: theme.textSecondary,
    margin: 0
  };

  const navStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 0'
  };

  const navItemStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 20px',
    margin: '2px 8px',
    borderRadius: '8px',
    backgroundColor: active ? (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)') : 'transparent',
    color: active ? colors.blue : theme.text,
    fontSize: '1rem',
    fontWeight: active ? '600' : '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    border: 'none',
    width: 'calc(100% - 16px)',
    textAlign: 'left',
    minHeight: '48px' // Touch-friendly
  });

  const iconStyle = {
    fontSize: '1.2rem',
    width: '24px',
    textAlign: 'center'
  };

  const footerStyle = {
    borderTop: `1px solid ${theme.border}`,
    padding: '16px'
  };

  const userInfoStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px',
    marginBottom: '12px',
    backgroundColor: theme.backgroundSecondary,
    borderRadius: '8px'
  };

  const userNameStyle = {
    fontSize: '0.95rem',
    fontWeight: '500',
    color: theme.text
  };

  const roleBadgeStyle = {
    fontSize: '0.7rem',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '10px',
    backgroundColor: isAdmin ? colors.blue : colors.green,
    color: (!isAdmin && darkMode) ? '#1a1a2e' : '#fff',
    textTransform: 'uppercase'
  };

  const footerButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px',
    marginBottom: '8px',
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: theme.text,
    fontSize: '0.95rem',
    fontWeight: '500',
    cursor: 'pointer',
    minHeight: '48px'
  };

  const logoutButtonStyle = {
    ...footerButtonStyle,
    backgroundColor: colors.red,
    borderColor: colors.red,
    color: darkMode ? '#1a1a2e' : '#fff',
    marginBottom: 0
  };

  // =============================================
  // RENDER
  // =============================================

  return (
    <>
      {/* Backdrop */}
      <div 
        style={backdropStyle} 
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <nav 
        style={drawerStyle}
        aria-label="Mobile navigation"
        role="navigation"
      >
        {/* Header with Logo */}
        <div style={headerStyle}>
          <img 
            src="/assets/logos/logo-square.png" 
            alt="CPLC Logo" 
            style={logoStyle}
          />
          <div style={headerTextStyle}>
            <h2 style={titleStyle}>Employee Portal</h2>
            <p style={subtitleStyle}>Creative Play Learning Center</p>
          </div>
        </div>

        {/* Navigation Items */}
        <div style={navStyle}>
          {menuItems.map((item) => (
            <button
              key={item.path}
              style={navItemStyle(isActive(item.path))}
              onClick={() => handleNavClick(item.path)}
            >
              <span style={iconStyle}>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          {/* User Info */}
          {user && (
            <div style={userInfoStyle}>
              <span style={{ fontSize: '1.2rem' }}>👤</span>
              <span style={userNameStyle}>{user.name}</span>
              <span style={roleBadgeStyle}>
                {isAdmin ? 'Admin' : 'Teacher'}
              </span>
            </div>
          )}

          {/* Dark Mode Toggle */}
          <button 
            style={footerButtonStyle}
            onClick={toggleDarkMode}
          >
            <span>{darkMode ? '☀️' : '🌙'}</span>
            <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>

          {/* Logout */}
          <button 
            style={logoutButtonStyle}
            onClick={handleLogout}
          >
            <span>↪</span>
            <span>Sign Out</span>
          </button>
        </div>
      </nav>
    </>
  );
}

export default MobileNavDrawer;
